import { requestTurnstileToken } from "../../../../libs/shared-ux/src/utilities/turnstile";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import type {
  AddEventRegistrationResultDto,
  ChargeMultipleProductsItemDto,
  ChargeProductResultDto,
  CreateGiftCardPurchaseIntentDto,
  DeferredChargeSettingsDto,
  GiftCardPurchaseIntentResponseDto,
  GetOAuthLinkResponse,
  RegisterFamilyMemberRequestDto,
  RegisterGuestRequestDto,
  RegisterSelfRequestDto,
  ResultDto,
  ScheduledAppointmentRequestDto,
} from "@zipper/embedded-api-client";
import type { EmbedStripe } from "../config/types";
import type { EmbeddedApiClients } from "../data/embedded-api";
import { getEmbeddedApiClients } from "../data/embedded-api";

/**
 * Real Stripe checkout for embeds, using Stripe Connect + the anon guest
 * endpoints on the NestJS API (same origin as the embed config endpoint, which
 * has global CORS). This mirrors the first-party web app: platform publishable
 * key + the org's connected account, card collected via Stripe Elements →
 * createPaymentMethod, then charged server-side.
 *
 * No mock path: if the org hasn't connected Stripe, callers get no `stripe`
 * config and must surface that instead of charging.
 */

// Cache the Stripe instance per (key, account) so repeated mounts reuse it.
const stripeCache = new Map<string, Promise<Stripe | null>>();

export function loadEmbedStripe(cfg: EmbedStripe): Promise<Stripe | null> {
  const cacheKey = `${cfg.publishableKey}::${cfg.accountId}`;
  let p = stripeCache.get(cacheKey);
  if (!p) {
    p = loadStripe(cfg.publishableKey, { stripeAccount: cfg.accountId });
    stripeCache.set(cacheKey, p);
  }
  return p;
}

export interface ChargeItem {
  deferredChargeSettings?: DeferredChargeSettingsDto | undefined;
  stripeProductId: string;
  /** Amount in dollars (the API expects dollars). */
  amount: number;
  quantity?: number | undefined;
  customDescription?: string | undefined;
  /** Reuse an existing Stripe coupon (validated promo) — applied invoice-wide. */
  coupon?: { id: string } | undefined;
  /**
   * Response to the product's `prior`-trigger form, if it has one.
   *
   * `RosterChargeService.validateRequiredForms` checks this FIRST, then falls
   * back to any response the buyer's roster already owns. The embeds never sent
   * it, so a first-time buyer who had just completed the form in this very
   * checkout was still rejected with "This product requires a form to be
   * completed before purchase."
   */
  formResponseId?: string | undefined;
  /**
   * The appointment type this line is booking, and the provider offering it.
   *
   * Without these the server prices the line off the bare drop-in product, and
   * that product does NOT carry the org's "pass fees to customer" switch — the
   * switch lives on the appointment type, because the drop-in product is minted
   * implicitly the first time somebody buys the service. So an appointment type
   * with fees switched ON was quoted, and charged, with no fee at all. Sending
   * the context lets `RosterChargeService` resolve both the fee flag and the
   * type's price the same way the in-app booking page does.
   */
  appointmentTypeId?: string | undefined;
  providerUserId?: number | undefined;
  appointmentDate?: string | undefined;
  appointmentTargetUserId?: number | null | undefined;
}

export interface GuestDetails {
  firstName: string;
  lastName: string;
  email: string;
  /**
   * PLAT-4456 fields an org can mark required at signup
   * (`Org.attributes.requiredSignupFields`). Optional here because whether they
   * are required is org policy, which `create-guest` checks server-side —
   * guest checkout builds its roster directly and never runs the signup
   * validation, so the API rejects the call rather than trusting the embed.
   */
  dateOfBirth?: string | undefined;
  emergencyContactName?: string | undefined;
  emergencyContactPhone?: string | undefined;
  /**
   * False for a secondary roster this checkout also creates — an extra event
   * attendee, a gift recipient. Only the primary customer is held to the org's
   * required-signup-fields policy, matching web signup (where family members
   * added during registration are not validated either). Omitted = primary.
   */
  isPrimaryCustomer?: boolean | undefined;
}

/**
 * The email given to guest checkout already belongs to a member of this org.
 * Anonymous checkout can't continue, but signing in can. Mirrors the API's
 * `GUEST_CHECKOUT_ACCOUNT_EXISTS_CODE`.
 */
export const GUEST_CHECKOUT_ACCOUNT_EXISTS_CODE =
  'guest_checkout_account_exists';

/** Error carrying the API's HTTP status and machine-readable `code`. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Read the API's machine-readable `code` off whatever the failing call threw.
 *
 * The generated SDK does not throw {@link ApiError} — it throws its own
 * `ResponseError`, carrying the parsed body on `.body`. Matching only on
 * `ApiError` therefore never recognised a real 401 from `create-guest`: an
 * existing member typing their email got the raw
 * "POST …/create-guest failed (401)" instead of the sign-in offer. Both shapes
 * are read here so the check works on real responses as well as hand-built ones.
 */
function apiErrorCode(error: unknown): string | undefined {
  if (error instanceof ApiError) return error.code;
  if (!error || typeof error !== 'object') return undefined;
  const body = (error as { body?: unknown }).body;
  if (!body || typeof body !== 'object') return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export function isAccountExistsError(error: unknown): boolean {
  return apiErrorCode(error) === GUEST_CHECKOUT_ACCOUNT_EXISTS_CODE;
}

function apiOrDefault(
  api: EmbeddedApiClients | undefined,
  orgId: number,
): EmbeddedApiClients {
  return api ?? getEmbeddedApiClients(orgId);
}

function chargeItem(item: ChargeItem): ChargeMultipleProductsItemDto {
  return {
    stripeProductId: item.stripeProductId,
    amount: item.amount,
    quantity: item.quantity ?? 1,
    ...(item.customDescription === undefined
      ? {}
      : { customDescription: item.customDescription }),
    ...(item.formResponseId === undefined
      ? {}
      : { formResponseId: item.formResponseId }),
    ...(item.appointmentTypeId === undefined
      ? {}
      : { appointmentTypeId: item.appointmentTypeId }),
    ...(item.providerUserId === undefined
      ? {}
      : { providerUserId: item.providerUserId }),
    ...(item.appointmentDate === undefined ? {} : { appointmentDate: item.appointmentDate }),
    ...(item.appointmentTargetUserId === undefined ? {} : { appointmentTargetUserId: item.appointmentTargetUserId }),
    ...(item.coupon === undefined
      ? {}
      : {
          coupon: {
            id: item.coupon.id,
            // The API treats an existing id as authoritative and ignores the
            // create-only fields. OpenAPI currently marks those fields
            // required, so provide inert values instead of escaping the DTO.
            appliesToProductIds: [],
            numberOfRedemptions: 0,
            amount: 0,
          },
        }),
  };
}

function assertChargeLinesAccepted(
  lines: ChargeProductResultDto[],
): ChargeProductResultDto[] {
  const rejected = lines.find((line) => !line.valid);
  if (rejected) {
    throw new Error(
      rejected.errorMessage ?? "The server rejected this charge line",
    );
  }
  return lines;
}

/**
 * Create (or find) a guest roster + Stripe customer for the org.
 *
 * E4: `CreateGuestRosterDto` accepts an optional `phone`, which the API
 * normalizes to E.164 and writes to `roster.attributes.phoneNumber` — i.e. the
 * phone entered at checkout lands on the customer profile. Callers that
 * collected one must forward it or the profile is created without a number.
 */
/**
 * A guest roster that already exists for this checkout.
 *
 * `create-guest` is find-or-401, NOT find-or-reuse: a second call for the same
 * email in the same org fails with `guest_checkout_account_exists`. So once a
 * flow has minted one it must carry the handle forward rather than ask again —
 * see {@link runGuestCheckout}'s `guestRoster`.
 */
export interface GuestRosterHandle {
  userId: number;
  rosterId: number;
  stripeCustomerId: string;
  checkoutToken: string;
}

/**
 * Guest rosters already minted (or in flight) on this page, keyed by org +
 * person. Because `create-guest` 401s on its second call for an email, every
 * embed surface that needs the buyer's identity — the host checkout, the
 * payment step's quote, post-charge registration — must share one call. Holding
 * that here, rather than asking each component to pass handles around, means a
 * new caller cannot reintroduce the double call.
 *
 * Only a settled success is kept; a failure is evicted so the caller can retry.
 * Name is part of the key so a differently named attendee who (wrongly)
 * reuses the buyer's email still gets the API's answer, not the buyer's roster.
 */
const guestRosterCalls = new Map<string, Promise<GuestRosterHandle>>();

function guestRosterKey(
  orgId: number,
  guest: Pick<GuestDetails, "firstName" | "lastName" | "email">,
): string {
  const norm = (value: string) => value.trim().toLowerCase();
  return [
    orgId,
    norm(guest.email),
    norm(guest.firstName),
    norm(guest.lastName),
  ].join("\u0000");
}

/** Test seam: forget every guest roster minted on this page. */
export function resetGuestRosterCache(): void {
  guestRosterCalls.clear();
}

export function createGuestRoster(
  orgId: number,
  guest: GuestDetails & { phone?: string | undefined },
  api?: EmbeddedApiClients,
): Promise<GuestRosterHandle> {
  const key = guestRosterKey(orgId, guest);
  const existing = guestRosterCalls.get(key);
  if (existing) return existing;
  const call = requestGuestRoster(orgId, guest, api).catch((error: unknown) => {
    guestRosterCalls.delete(key);
    throw error;
  });
  guestRosterCalls.set(key, call);
  return call;
}

async function requestGuestRoster(
  orgId: number,
  guest: GuestDetails & { phone?: string | undefined },
  api?: EmbeddedApiClients,
): Promise<GuestRosterHandle> {
  const clients = apiOrDefault(api, orgId);
  const turnstileToken = clients.surface === 'public-embed'
    ? undefined
    : await requestTurnstileToken(async () => {
        if (!clients.turnstile) throw new Error('Human verification is unavailable. Please retry or contact the studio.');
        return clients.turnstile.getTurnstileConfig();
      }, 'guest');
  return clients.roster.createGuestRosterForCheckout({
    orgId,
    createGuestRosterDto: {
      ...(turnstileToken === undefined ? {} : { turnstileToken }),
      firstName: guest.firstName,
      lastName: guest.lastName,
      email: guest.email,
      ...(guest.phone === undefined ? {} : { phone: guest.phone }),
      ...(guest.dateOfBirth === undefined
        ? {}
        : { dateOfBirth: guest.dateOfBirth }),
      ...(guest.emergencyContactName === undefined
        ? {}
        : { emergencyContactName: guest.emergencyContactName }),
      ...(guest.emergencyContactPhone === undefined
        ? {}
        : { emergencyContactPhone: guest.emergencyContactPhone }),
      ...(guest.isPrimaryCustomer === undefined
        ? {}
        : { isPrimaryCustomer: guest.isPrimaryCustomer }),
    },
  });
}

/** Attach a tokenized card (PaymentMethod) to the guest's customer. */
export function addGuestPaymentMethod(
  orgId: number,
  rosterId: number,
  paymentMethodId: string,
  checkoutToken: string,
  api?: EmbeddedApiClients,
) {
  return apiOrDefault(api, orgId).roster.addPaymentMethodGuest({
    orgId,
    addGuestPaymentMethodDto: { rosterId, paymentMethodId, checkoutToken },
  });
}

/**
 * Open an ACH (Financial Connections) SetupIntent for the paying roster.
 *
 * A guest proves ownership with the `create-guest` capability; a member is
 * authorized by their customer JWT. Either way the server picks the Stripe
 * customer, so the verified bank account is saved where the charge expects it.
 */
export async function createAchSetupIntent(
  orgId: number,
  owner:
    | { kind: "guest"; rosterId: number; checkoutToken: string }
    | { kind: "member"; rosterId: number },
  api?: EmbeddedApiClients,
): Promise<string> {
  const roster = apiOrDefault(api, orgId).roster;
  const { clientSecret } =
    owner.kind === "guest"
      ? await roster.createGuestACHSetupIntent({
          orgId,
          createGuestACHSetupIntentDto: {
            rosterId: owner.rosterId,
            checkoutToken: owner.checkoutToken,
          },
        })
      : await roster.createMemberACHSetupIntent({
          orgId,
          createMemberACHSetupIntentDto: { rosterId: owner.rosterId },
        });
  return clientSecret;
}

/** One result line per charged item (aligned to `items` order). */
export type ChargeResultLine = Pick<
  ChargeProductResultDto,
  "valid" | "invoiceId" | "paymentIntentId"
>;

/** Charge the guest for one or more products (executes the charge). */
export function chargeGuest(
  orgId: number,
  params: {
    items: ChargeItem[];
    paymentMethodId: string;
    paymentMethodOwnerStripeCustomerId: string;
    purchasedForRosterId: number;
    chargeAttemptId?: string | undefined;
    /** Capability from `createGuestRoster`; the charge route requires it. */
    checkoutToken: string;
    /** Fee rail; the server prices as card when omitted. */
    paymentMethodType?: string | undefined;
  },
  api?: EmbeddedApiClients,
): Promise<ChargeResultLine[]> {
  return apiOrDefault(api, orgId)
    .roster.chargeMultipleProductsGuest({
      orgId,
      chargeMultipleProductsDto: {
        checkoutToken: params.checkoutToken,
        items: params.items.map(chargeItem),
        paymentMethodId: params.paymentMethodId,
        paymentMethodOwnerStripeCustomerId:
          params.paymentMethodOwnerStripeCustomerId,
        purchasedForRosterId: params.purchasedForRosterId,
        ...(params.chargeAttemptId === undefined
          ? {}
          : { chargeAttemptId: params.chargeAttemptId }),
        ...(params.paymentMethodType === undefined
          ? {}
          : { paymentMethodType: params.paymentMethodType }),
      },
    })
    .then(assertChargeLinesAccepted);
}

/**
 * Price a cart without charging it.
 *
 * Hits the same charge path the payment will take, with `executeCharge: false`
 * — so the tax Stripe will assess and the platform fee an org passes on
 * (`passFeesToCustomer`) come back as real numbers rather than being guessed at
 * in the browser. The embeds have to ask the server for these: Stripe Tax
 * depends on the customer's resolved location, and the fee depends on the org's
 * payment tier and the payment method type.
 *
 * Guest or member is chosen the same way the charge is (and the same way the
 * web app's `getPublicCheckoutChargeTotal` does it): a capability token means
 * an anonymous buyer and the guest route, otherwise the signed-in member's own
 * route, which verifies their roster access.
 *
 * Requires a roster — the quote is drafted against it. Callers must create the
 * guest roster before previewing.
 */
export function previewCharge(
  params: {
    orgId: number;
    items: ChargeItem[];
    /** The roster the quote is drafted against. */
    purchasedForRosterId: number;
    /** Guest capability from {@link createGuestRoster}; omit for a member. */
    checkoutToken?: string | undefined;
    /** Owner of the card being used, when one is already known. */
    stripeCustomerId?: string | undefined;
    paymentMethodId?: string | undefined;
    /**
     * `card` or `us_bank_account`. The fee differs by rail, so quoting without
     * it quotes the default (card) rate.
     */
    paymentMethodType?: string | undefined;
  },
  api?: EmbeddedApiClients,
): Promise<ChargeProductResultDto[]> {
  const chargeMultipleProductsDto = {
    items: params.items.map(chargeItem),
    // Resolved server-side from the roster when the buyer has no card yet.
    paymentMethodOwnerStripeCustomerId: params.stripeCustomerId ?? "",
    purchasedForRosterId: params.purchasedForRosterId,
    ...(params.paymentMethodId === undefined
      ? {}
      : { paymentMethodId: params.paymentMethodId }),
    ...(params.paymentMethodType === undefined
      ? {}
      : { paymentMethodType: params.paymentMethodType }),
  };
  const roster = apiOrDefault(api, params.orgId).roster;
  return params.checkoutToken
    ? roster.getMultipleChargeTotalGuest({
        orgId: params.orgId,
        chargeMultipleProductsDto: {
          ...chargeMultipleProductsDto,
          checkoutToken: params.checkoutToken,
        },
      })
    : roster.getMultipleChargeTotal({
        orgId: params.orgId,
        chargeMultipleProductsDto,
      });
}

/**
 * Full one-shot guest checkout: create roster → attach card → charge.
 * Returns when the charge has executed (throws on any failure).
 */
export async function runGuestCheckout(params: {
  orgId: number;
  /** `phone` (when collected) is persisted to the guest's roster — see {@link createGuestRoster}. */
  guest: GuestDetails & { phone?: string | undefined };
  paymentMethodId: string;
  items: ChargeItem[];
  chargeAttemptId?: string | undefined;
  /** When set, the product is assigned to this roster (guest/family purchase). */
  purchasedForRosterId?: number | undefined;
  /**
   * A roster this flow already created (booking checkout mints one up front so
   * intake forms can be filed against it). Reused as-is: calling `create-guest`
   * a second time for the same email would 401, not return the same roster.
   */
  guestRoster?: GuestRosterHandle | undefined;
  /**
   * The method is already on the guest's customer — an ACH account a
   * SetupIntent verified and attached — so skip the attach step, as the
   * dashboard checkout does for ACH.
   */
  paymentMethodAttached?: boolean | undefined;
  /**
   * Must match the rail the quote priced. Without it the server charges the
   * card fee even for a bank account.
   */
  paymentMethodType?: string | undefined;
  api?: EmbeddedApiClients | undefined;
}): Promise<{ rosterId: number; invoiceIds: (string | undefined)[] }> {
  const { rosterId, stripeCustomerId, checkoutToken } =
    params.guestRoster ??
    (await createGuestRoster(params.orgId, params.guest, params.api));
  if (!params.paymentMethodAttached) {
    await addGuestPaymentMethod(
      params.orgId,
      rosterId,
      params.paymentMethodId,
      checkoutToken,
      params.api,
    );
  }
  const beneficiaryRosterId = params.purchasedForRosterId ?? rosterId;
  const lines = await chargeGuest(
    params.orgId,
    {
      items: params.items,
      paymentMethodId: params.paymentMethodId,
      paymentMethodOwnerStripeCustomerId: stripeCustomerId,
      purchasedForRosterId: beneficiaryRosterId,
      chargeAttemptId: params.chargeAttemptId,
      checkoutToken,
      paymentMethodType: params.paymentMethodType,
    },
    params.api,
  );
  // Align invoice ids to items order so each registration can cite its payment.
  const invoiceIds = params.items.map((_, i) => lines[i]?.invoiceId);
  return { rosterId, invoiceIds };
}

/** $0 product purchase — no card required (C723-28). */
export async function runFreePurchase(params: {
  orgId: number;
  guest: GuestDetails & { phone?: string | undefined };
  items: ChargeItem[];
  memberContext?: { rosterId: number; stripeCustomerId: string } | undefined;
  /** PLAT-5624: idempotency key — a duplicate $0 purchase still double-grants. */
  chargeAttemptId?: string | undefined;
  purchasedForRosterId?: number | undefined;
  /** Reuse an already-created guest roster — see {@link runGuestCheckout}. */
  guestRoster?: GuestRosterHandle | undefined;
  api?: EmbeddedApiClients | undefined;
}): Promise<{ rosterId: number; invoiceIds: (string | undefined)[] }> {
  const items = params.items.map(chargeItem);

  if (params.memberContext) {
    const lines = assertChargeLinesAccepted(
      await apiOrDefault(params.api, params.orgId).roster.chargeMultipleProducts({
        orgId: params.orgId,
        chargeMultipleProductsDto: {
          items,
          paymentMethodId: "",
          paymentMethodOwnerStripeCustomerId:
            params.memberContext.stripeCustomerId,
          purchasedForRosterId:
            params.purchasedForRosterId ?? params.memberContext.rosterId,
          ...(params.chargeAttemptId === undefined
            ? {}
            : { chargeAttemptId: params.chargeAttemptId }),
        },
      }),
    );
    const invoiceIds = params.items.map((_, i) => lines[i]?.invoiceId);
    return { rosterId: params.memberContext.rosterId, invoiceIds };
  }

  const { rosterId, stripeCustomerId, checkoutToken } =
    params.guestRoster ??
    (await createGuestRoster(params.orgId, params.guest, params.api));
  const beneficiaryRosterId = params.purchasedForRosterId ?? rosterId;
  const lines = await chargeGuest(
    params.orgId,
    {
      items: params.items,
      paymentMethodId: "",
      paymentMethodOwnerStripeCustomerId: stripeCustomerId,
      purchasedForRosterId: beneficiaryRosterId,
      chargeAttemptId: params.chargeAttemptId,
      checkoutToken,
    },
    params.api,
  );
  const invoiceIds = params.items.map((_, i) => lines[i]?.invoiceId);
  return { rosterId, invoiceIds };
}

// ── Member checkout (Phase 4) ──
// Authenticated checkout for saved cards and freshly-created payment methods.
// It never runs for guests and sends the embed JWT.
// The guest path (createGuestRoster → addGuestPaymentMethod → chargeGuest) is
// left exactly as-is.
//
// It reuses the EXISTING authenticated charge endpoint
// (`POST /org/:orgId/roster/packages/charge-multiple`, `@ZApiUserRoute`) rather
// than a new charge route — the embed JWT is a standard customer token, so this
// adds NO new charge-path code on the backend. Body matches
// `ChargeMultipleProductsDto`; the coupon rides on the first item (same as the
// guest path), and the member's roster + customer come from the member context.

export interface MemberCheckoutParams {
  orgId: number;
  /** The signed-in member's roster (from the member context). */
  rosterId: number;
  /** The member's Stripe customer id that owns the payment method. */
  stripeCustomerId: string;
  /** A saved or freshly-created PaymentMethod id. */
  paymentMethodId: string;
  /** Attach a freshly-created card to the authenticated payer before charging. */
  attachPaymentMethod?: boolean | undefined;
  items: ChargeItem[];
  chargeAttemptId?: string | undefined;
  /** When set, credits/product land on this roster instead of the payer's. */
  purchasedForRosterId?: number | undefined;
  /** Fee rail — see {@link runGuestCheckout}. */
  paymentMethodType?: string | undefined;
  api?: EmbeddedApiClients | undefined;
}

/**
 * Charge a signed-in member via the existing authenticated
 * charge endpoint (JWT in the Authorization header). Returns invoice ids aligned
 * to `items`, mirroring {@link runGuestCheckout}'s shape so callers/after-charge
 * hooks are interchangeable.
 */
export async function runMemberCheckout(
  params: MemberCheckoutParams,
): Promise<{ rosterId: number; invoiceIds: (string | undefined)[] }> {
  if (params.attachPaymentMethod) {
    await apiOrDefault(params.api, params.orgId).roster.addPaymentMethod({
      rosterId: params.rosterId,
      paymentMethodDto: {
        id: params.paymentMethodId,
        type: 'card',
        label: 'Card',
        isDefault: false,
        activeSubscriptions: [],
      },
    });
  }
  const lines = assertChargeLinesAccepted(
    await apiOrDefault(params.api, params.orgId).roster.chargeMultipleProducts({
      orgId: params.orgId,
      chargeMultipleProductsDto: {
        items: params.items.map(chargeItem),
        paymentMethodId: params.paymentMethodId,
        paymentMethodOwnerStripeCustomerId: params.stripeCustomerId,
        purchasedForRosterId: params.purchasedForRosterId ?? params.rosterId,
        ...(params.chargeAttemptId === undefined
          ? {}
          : { chargeAttemptId: params.chargeAttemptId }),
        ...(params.paymentMethodType === undefined
          ? {}
          : { paymentMethodType: params.paymentMethodType }),
      },
    }),
  );
  const invoiceIds = params.items.map((_, i) => lines[i]?.invoiceId);
  return { rosterId: params.rosterId, invoiceIds };
}

/** Gift-card purchase intent (in-app flow). */
export type GiftPurchaseIntent = GiftCardPurchaseIntentResponseDto;

/**
 * Create a gift-card purchase intent (in-app/embedded flow). Returns the gift
 * card id + the Stripe product/amount to charge via the guest checkout. After
 * the charge succeeds, call {@link completeGiftCard} with the invoice id to
 * activate the gift and email the recipient.
 */
export function createGiftPurchaseIntent(
  body: Omit<CreateGiftCardPurchaseIntentDto, "recipientEmail" | "message"> & {
    recipientEmail?: string | undefined;
    message?: string | undefined;
  },
  api?: EmbeddedApiClients,
): Promise<GiftPurchaseIntent> {
  const { recipientEmail, message, ...requiredBody } = body;
  return apiOrDefault(api, body.orgId).giftCards.createPurchaseIntent({
    createGiftCardPurchaseIntentDto: {
      ...requiredBody,
      ...(recipientEmail === undefined ? {} : { recipientEmail }),
      ...(message === undefined ? {} : { message }),
    },
  });
}

/** Activate a paid gift card and (optionally) email the recipient their claim. */
export function completeGiftCard(
  orgId: number,
  giftCardId: string,
  invoiceId: string,
  sendToRecipient = true,
  api?: EmbeddedApiClients,
): Promise<ResultDto> {
  return apiOrDefault(api, orgId).giftCards.inAppCompleteGiftCard({
    id: giftCardId,
    invoiceId,
    sendToRecipient: String(sendToRecipient),
  });
}

/** A bookable appointment slot, as returned by the availabilities endpoint. */
export type AppointmentTimeslot =
  ScheduledAppointmentRequestDto["timeSlots"][number];

/**
 * Create a guest appointment after payment, via the same anonymous endpoint
 * multisite books against (`appointments-no-auth`). `paymentCompleted: true`
 * marks it paid (the guest charge ran first). Guest identity is form-driven on
 * the backend, so `formSubmissions`/`formResponseId` carry the intake details.
 */
export function createAppointment(
  orgId: number,
  providerUserId: number,
  body: ScheduledAppointmentRequestDto,
  api?: EmbeddedApiClients,
): Promise<GetOAuthLinkResponse> {
  return apiOrDefault(api, orgId).schedule.scheduleAppointment({
    orgId,
    providerUserId,
    scheduledAppointmentRequestDto: body,
  });
}

/** @deprecated Use {@link createAppointment} */
export const createGuestAppointment = createAppointment;

/**
 * Register a guest for a class/event after payment. The paid `stripeInvoiceId`
 * is proof of payment; `spotHoldId` (if any) confirms the held seat into a
 * permanent booking. Mirrors the first-party post-charge registration step.
 *
 * A signed-in member can instead redeem a credit-pack they already hold by
 * passing `purchasedForRosterId` + `consumeCreditForProductId` (no invoice):
 * the API resolves and consumes the roster's own credit for that product, so a
 * fully-covered registration never manufactures a $0 charge.
 *
 * When the seat is for someone else, `purchasedForRosterId` names the attendee
 * and `consumeCreditFromRosterId` names whose pack pays for it. The API only
 * spends another roster's credits for a caller holding that roster's own
 * bearer token, and only when the product allows sharing.
 */
export function registerGuest(
  orgId: number,
  orgSlug: string,
  eventSlug: string,
  body: RegisterGuestRequestDto,
  api?: EmbeddedApiClients,
): Promise<AddEventRegistrationResultDto> {
  return apiOrDefault(api, orgId).eventRegister.registerAsGuest({
    orgSlug,
    eventSlug,
    registerGuestRequestDto: body,
  });
}

/** Signed-in member registers themselves for a class/event after payment. */
export function registerSelf(
  orgId: number,
  orgSlug: string,
  eventSlug: string,
  body: RegisterSelfRequestDto,
  api?: EmbeddedApiClients,
): Promise<AddEventRegistrationResultDto> {
  return apiOrDefault(api, orgId).eventRegister.registerSelf({
    orgSlug,
    eventSlug,
    registerSelfRequestDto: body,
  });
}

/** Signed-in member registers a family member (by user id) after payment. */
export function registerFamilyMember(
  orgId: number,
  orgSlug: string,
  eventSlug: string,
  body: RegisterFamilyMemberRequestDto,
  api?: EmbeddedApiClients,
): Promise<AddEventRegistrationResultDto> {
  return apiOrDefault(api, orgId).eventRegister.registerFamilyMember({
    orgSlug,
    eventSlug,
    registerFamilyMemberRequestDto: body,
  });
}
